import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserProfile } from '../types';
import { CameraIcon, ChevronDownIcon } from './icons';
import { uploadUserAvatar } from '../services/avatarStorage';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import { RODDO_LANGUAGE_LABELS, normalizeRoddoLanguageCode, setRoddoLanguage, type RoddoSupportedLanguage } from '../../i18n';
import {
  FISHIQ_DATA_PROMISE_VERSION,
  LEGAL_SCHEMA_VERSION,
} from '../legal/legalText';

interface EditProfileViewProps {
  userProfile: UserProfile;
  onOpenAdmin?: () => void;
  onProfileUpdate: (updatedProfile: Partial<UserProfile>) => Promise<void> | void;
  onCancel: () => void;
  addToast?: (message: string, type?: 'success' | 'error' | 'info') => void;
  isDarkMode?: boolean;
}

type SaveState = 'idle' | 'saving';

const safeDisplayName = (profile: UserProfile) =>
  (profile as any)?.displayName || (profile as any)?.username || profile.name || 'Angler';

type RoddoLanguageCode = RoddoSupportedLanguage;

const RODDO_LANGUAGES: Array<{ code: RoddoLanguageCode; name: string; noteKey: string; fallbackNote: string }> = [
  { code: 'en', name: RODDO_LANGUAGE_LABELS.en, noteKey: 'editProfile.englishNote', fallbackNote: 'Fully supported first.' },
  { code: 'es', name: RODDO_LANGUAGE_LABELS.es, noteKey: 'editProfile.spanishNote', fallbackNote: 'Foundation ready; more translated screens coming.' },
  { code: 'ko', name: RODDO_LANGUAGE_LABELS.ko, noteKey: 'editProfile.koreanNote', fallbackNote: 'Foundation ready; more translated screens coming.' },
];

const resolveProfileLanguageCode = (profile: any): RoddoLanguageCode =>
  normalizeRoddoLanguageCode(
    profile?.preferredLanguage ??
      profile?.language ??
      profile?.locale ??
      profile?.appLanguage ??
      profile?.settings?.language,
  );

const resolveProfileAvatar = (profile: Partial<UserProfile> | any): string =>
  String(
    profile?.avatar ||
      profile?.avatarUrl ||
      profile?.avatarURL ||
      profile?.photoURL ||
      profile?.photoUrl ||
      profile?.profilePhotoUrl ||
      profile?.imageUrl ||
      '',
  ).trim();

const LanguageSwitcher: React.FC<{
  ui: any;
  inputStyle: React.CSSProperties;
  panelStyle: React.CSSProperties;
  value: RoddoLanguageCode;
  onChange: (code: RoddoLanguageCode) => void;
}> = ({ ui, inputStyle, panelStyle, value, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const activeLang = RODDO_LANGUAGES.find((lang) => lang.code === value) ?? RODDO_LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const chooseLanguage = (code: RoddoLanguageCode) => {
    const nextLanguage = normalizeRoddoLanguageCode(code);
    // Important: only update the local form draft here. App.tsx syncs i18n
    // from the saved userProfile language, so changing global i18n before save
    // can be immediately overwritten by the still-old profile snapshot.
    onChange(nextLanguage);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-3 rounded-[14px] px-4 py-3 text-left text-sm font-black outline-none transition active:translate-y-[1px]"
        style={inputStyle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('editProfile.languageAria', 'Roddo app language')}
      >
        <span className="min-w-0">
          <span className="block truncate">{activeLang.name}</span>
          <span className="mt-0.5 block truncate text-[11px] font-semibold" style={{ color: ui.textSecondary }}>
            {t(activeLang.noteKey, activeLang.fallbackNote)}
          </span>
        </span>
        <ChevronDownIcon
          className="h-5 w-5 shrink-0 transition-transform"
          style={{ color: ui.textSecondary, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {open ? (
        <div
          className="absolute left-0 right-0 z-30 mt-2 overflow-hidden rounded-[12px] p-1 shadow-[0_10px_28px_rgba(0,0,0,0.28)]"
          style={panelStyle}
          role="listbox"
          aria-label={t('editProfile.languageAria', 'Roddo app language')}
        >
          {RODDO_LANGUAGES.map((lang) => {
            const active = value === lang.code;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => chooseLanguage(lang.code)}
                className="flex w-full items-center justify-between gap-3 rounded-[13px] px-3 py-2.5 text-left transition active:translate-y-[1px]"
                style={{
                  background: active ? 'rgba(103,216,232,0.07)' : 'transparent',
                  color: ui.textPrimary,
                }}
                role="option"
                aria-selected={active}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-black">{lang.name}</span>
                  <span className="mt-0.5 block text-[11px] font-semibold leading-4" style={{ color: ui.textSecondary }}>
                    {t(lang.noteKey, lang.fallbackNote)}
                  </span>
                </span>
                {active ? <span className="shrink-0 text-sm font-black">✓</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="mt-2 space-y-1">
        {RODDO_LANGUAGES.map((lang) => {
          const active = value === lang.code;
          return (
            <p
              key={lang.code}
              className="text-[11px] font-semibold leading-4"
              style={{ color: active ? ui.textPrimary : ui.textSecondary, opacity: active ? 0.96 : 0.62 }}
            >
              {active ? `${t('editProfile.currentLanguage', 'Current')}: ` : ''}{lang.name} — {t(lang.noteKey, lang.fallbackNote)}
            </p>
          );
        })}
      </div>
    </div>
  );
};

const SettingsSection: React.FC<{
  eyebrow?: string;
  title: string;
  description?: string;
  children: React.ReactNode;
  style: React.CSSProperties;
  mutedColor: string;
  action?: React.ReactNode;
}> = ({ eyebrow, title, description, children, style, mutedColor, action }) => (
  <section className="rounded-[16px] p-4" style={style}>
    <div className="mb-3.5 flex items-start justify-between gap-3">
      <div className="min-w-0">
        {eyebrow && (
          <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: mutedColor }}>
            {eyebrow}
          </div>
        )}
        <h2 className="mt-1 text-base font-black tracking-[-0.01em] sm:text-lg">{title}</h2>
        {description && <p className="mt-1 max-w-2xl text-xs leading-5" style={{ color: mutedColor }}>{description}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const EditProfileView: React.FC<EditProfileViewProps> = ({
  userProfile,
  onOpenAdmin,
  onProfileUpdate,
  onCancel,
  addToast,
  isDarkMode,
}) => {
  const { t } = useTranslation();
  const isDark = resolveRoddoDarkMode(isDarkMode);
  const ui = getRoddoUiTokens(isDark) as any;

  const [name, setName] = useState(userProfile.name || safeDisplayName(userProfile));
  const [bio, setBio] = useState(userProfile.bio || '');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState(resolveProfileAvatar(userProfile));
  const [avatarLightboxOpen, setAvatarLightboxOpen] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [fishiqContributionEnabled, setFishiqContributionEnabled] = useState<boolean>(
    (userProfile as any)?.fishiqContributionEnabled === true ||
      (userProfile as any)?.legal?.consents?.fishiqContribution?.enabled === true,
  );
  const [preferredLanguage, setPreferredLanguage] = useState<RoddoLanguageCode>(
    resolveProfileLanguageCode(userProfile),
  );

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const initialRef = useRef({
    name: (userProfile.name || safeDisplayName(userProfile)).trim(),
    bio: (userProfile.bio || '').trim(),
    avatar: resolveProfileAvatar(userProfile),
    fishiqContributionEnabled:
      (userProfile as any)?.fishiqContributionEnabled === true ||
      (userProfile as any)?.legal?.consents?.fishiqContribution?.enabled === true,
    preferredLanguage: resolveProfileLanguageCode(userProfile),
  });

  const toast = (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    if (typeof addToast === 'function') addToast(msg, type);
    else console.log(`[EditProfileView toast:${type}]`, msg);
  };

  useEffect(() => {
    const nextName = userProfile.name || safeDisplayName(userProfile);
    const nextAvatar = resolveProfileAvatar(userProfile);

    setName(nextName);
    setBio(userProfile.bio || '');
    setAvatarPreview(nextAvatar);
    setAvatarFile(null);
    const nextFishiqContributionEnabled =
      (userProfile as any)?.fishiqContributionEnabled === true ||
      (userProfile as any)?.legal?.consents?.fishiqContribution?.enabled === true;
    setFishiqContributionEnabled(nextFishiqContributionEnabled);
    const nextPreferredLanguage = resolveProfileLanguageCode(userProfile);
    setPreferredLanguage(nextPreferredLanguage);

    initialRef.current = {
      name: nextName.trim(),
      bio: (userProfile.bio || '').trim(),
      avatar: nextAvatar.trim(),
      fishiqContributionEnabled: nextFishiqContributionEnabled,
      preferredLanguage: nextPreferredLanguage,
    };
  }, [userProfile]);

  useEffect(() => {
    if (!avatarLightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAvatarLightboxOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [avatarLightboxOpen]);

  const hasChanges = useMemo(() => {
    const init = initialRef.current;
    return (
      (name || '').trim() !== init.name ||
      (bio || '').trim() !== init.bio ||
      !!avatarFile ||
      (avatarPreview || '').trim() !== init.avatar ||
      fishiqContributionEnabled !== init.fishiqContributionEnabled ||
      preferredLanguage !== init.preferredLanguage
    );
  }, [name, bio, avatarFile, avatarPreview, fishiqContributionEnabled, preferredLanguage]);

  const avatarSrc = avatarPreview?.trim() || '/avatar-default.png';
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!userProfile || !hasChanges || saveState === 'saving') return;

    setSaveState('saving');
    try {
      let avatarUrl = resolveProfileAvatar(userProfile);
      let avatarUpdatedAt = Number((userProfile as any)?.avatarUpdatedAt || Date.now());

      if (avatarFile) {
        avatarUrl = await uploadUserAvatar({ userId: userProfile.uid, file: avatarFile });
        avatarUpdatedAt = Date.now();
      }

      const displayName = name.trim() || userProfile.displayName || userProfile.name || safeDisplayName(userProfile);
      const consentUpdatedAt = Date.now();
      const cleanedPayload: Partial<UserProfile> = {
        name: displayName,
        displayName,
        bio: bio.trim(),
        avatar: avatarUrl,
        avatarUrl,
        photoURL: avatarUrl,
        photoUrl: avatarUrl,
        profilePhotoUrl: avatarUrl,
        imageUrl: avatarUrl,
        avatarUpdatedAt,
        preferredLanguage,
        language: preferredLanguage,
        locale: preferredLanguage,
        appLanguage: preferredLanguage,
        fishiqContributionEnabled,
        fishiqContributionUpdatedAt: consentUpdatedAt,
        legal: {
          ...((userProfile as any)?.legal || {}),
          schemaVersion: LEGAL_SCHEMA_VERSION,
          consents: {
            ...((userProfile as any)?.legal?.consents || {}),
            fishiqContribution: {
              ...((userProfile as any)?.legal?.consents?.fishiqContribution || {}),
              enabled: fishiqContributionEnabled,
              version: FISHIQ_DATA_PROMISE_VERSION,
              acceptedAt: fishiqContributionEnabled
                ? (userProfile as any)?.legal?.consents?.fishiqContribution?.acceptedAt ??
                  consentUpdatedAt
                : null,
              declinedAt: fishiqContributionEnabled
                ? null
                : consentUpdatedAt,
              updatedAt: consentUpdatedAt,
            },
          },
        },
        updatedAt: Date.now(),
      } as any;

      // Flip the local preview to the final Storage URL before navigation, so the
      // current screen and the profile screen do not wait for a full reload.
      setAvatarPreview(avatarUrl);
      setAvatarFile(null);

      await onProfileUpdate(cleanedPayload);
      void setRoddoLanguage(preferredLanguage);

      initialRef.current = {
        name: displayName.trim(),
        bio: (cleanedPayload.bio ?? '').trim(),
        avatar: avatarUrl.trim(),
        fishiqContributionEnabled,
        preferredLanguage,
      };
      toast(t('editProfile.profileUpdated', 'Profile updated.'), 'success');
    } catch (err) {
      console.error('[EditProfileView] Failed to save profile:', err);
      toast(t('editProfile.saveError', 'Could not save changes. Try again.'), 'error');
    } finally {
      setSaveState('idle');
    }
  };


  const canOpenAdmin = !!(userProfile?.isAdmin && onOpenAdmin);
  const profileLevel = Number((userProfile as any)?.level ?? 1) || 1;
  const memberNumber = (userProfile as any)?.memberNumber;

  const pageStyle: React.CSSProperties = {
    color: ui.textPrimary,
    background: ui.pageBg ?? 'transparent',
  };

  const cardStyle: React.CSSProperties = {
    background: isDark ? '#101827' : '#FFFCF7',
    border: `1px solid ${isDark ? 'rgba(132,151,190,0.16)' : 'rgba(96,75,48,0.12)'}`,
    boxShadow: isDark ? '0 5px 14px rgba(0,0,0,0.14)' : '0 7px 18px rgba(91,65,37,0.07)',
    color: ui.textPrimary,
  };

  const subtleCardStyle: React.CSSProperties = {
    background: isDark ? '#0F1828' : 'rgba(255,255,255,0.68)',
    border: `1px solid ${isDark ? 'rgba(132,151,190,0.13)' : 'rgba(96,75,48,0.12)'}`,
    color: ui.textPrimary,
  };

  const inputStyle: React.CSSProperties = {
    background: isDark ? '#0C1423' : 'rgba(255,255,255,0.84)',
    border: `1px solid ${isDark ? 'rgba(132,151,190,0.16)' : 'rgba(96,75,48,0.16)'}`,
    color: ui.textPrimary,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.025)' : 'inset 0 1px 0 rgba(255,255,255,0.72)',
  };

  const quietButtonStyle: React.CSSProperties = {
    background: isDark ? '#0C1423' : 'rgba(255,255,255,0.76)',
    border: `1px solid ${isDark ? 'rgba(132,151,190,0.15)' : 'rgba(96,75,48,0.14)'}`,
    color: ui.textPrimary,
  };

  const primaryButtonStyle: React.CSSProperties = {
    background: isDark ? '#8A70F2' : '#2F80ED',
    color: '#FFFFFF',
    boxShadow: isDark ? '0 5px 14px rgba(65,52,145,0.18)' : '0 5px 14px rgba(47,128,237,0.16)',
  };

  const dangerButtonStyle: React.CSSProperties = {
    background: isDark ? 'rgba(248,113,113,0.14)' : 'rgba(254,242,242,0.96)',
    border: `1px solid ${isDark ? 'rgba(248,113,113,0.30)' : 'rgba(220,38,38,0.18)'}`,
    color: isDark ? '#FECACA' : '#991B1B',
  };

  const labelClass = 'mb-1.5 block text-[11px] font-black uppercase tracking-[0.16em]';
  const muted = ui.textSecondary;

  return (
    <div
      data-roddo-edit-profile="true"
      className="mx-auto min-w-0 max-w-2xl space-y-3 px-3 pb-6 pt-4 sm:px-4"
      style={pageStyle}
    >
      <section className="rounded-[16px] p-4 sm:p-5" style={cardStyle}>
        <div className="flex items-start justify-between gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-[22px] font-black leading-none transition active:translate-y-[1px]"
            style={quietButtonStyle}
            aria-label={t('common.back', 'Back')}
            title={t('common.back', 'Back')}
          >
            <span aria-hidden="true">‹</span>
          </button>

          <span
            className="rounded-full px-3 py-1.5 text-[11px] font-extrabold"
            style={{
              background: hasChanges ? 'rgba(255,215,106,0.20)' : (isDark ? 'rgba(125,211,252,0.12)' : 'rgba(236,253,245,0.90)'),
              border: `1px solid ${hasChanges ? 'rgba(245,158,11,0.25)' : (isDark ? 'rgba(125,211,252,0.18)' : 'rgba(16,185,129,0.16)')}`,
              color: hasChanges ? (isDark ? '#FDE68A' : '#92400E') : (isDark ? '#BAE6FD' : '#047857'),
            }}
          >
            {hasChanges ? t('editProfile.unsaved', 'Unsaved') : t('editProfile.synced', 'Synced')}
          </span>
        </div>

        <div className="mt-5 flex items-center gap-4">
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setAvatarLightboxOpen(true)}
              className="block overflow-hidden rounded-[18px] border-2 border-white/70 shadow-sm"
              aria-label={t('editProfile.previewAvatar', 'Preview avatar')}
            >
              <img src={avatarSrc} alt="Avatar" className="h-24 w-24 object-cover sm:h-28 sm:w-28" />
            </button>
            <input type="file" accept="image/*" ref={avatarInputRef} onChange={handleAvatarChange} className="hidden" aria-label={t('editProfile.uploadNewAvatar', 'Upload new avatar')} />
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              className="absolute -bottom-2 -right-2 grid h-10 w-10 place-items-center rounded-2xl text-sm font-black transition active:translate-y-[1px]"
              style={{ ...primaryButtonStyle, background: isDark ? '#67D8E8' : '#2F80ED', color: isDark ? '#071019' : '#FFFFFF' }}
              aria-label={t('editProfile.changeAvatar', 'Change avatar')}
            >
              <CameraIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: muted }}>{t('editProfile.profileSettings', 'Profile settings')}</div>
            <h1 className="mt-1 truncate text-2xl font-black tracking-[-0.03em] sm:text-3xl">{name.trim() || safeDisplayName(userProfile)}</h1>
            <p className="mt-1 text-sm leading-5" style={{ color: muted }}>
              {t('editProfile.updatePublicCard', 'Update your public angler card, language, and observation-sharing preferences.')}
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-extrabold">
              <span className="rounded-full px-3 py-1.5" style={subtleCardStyle}>{t('editProfile.level', 'Level {{level}}', { level: profileLevel })}</span>
              {memberNumber ? <span className="rounded-full px-3 py-1.5" style={subtleCardStyle}>{t('editProfile.memberNumber', 'Member #{{number}}', { number: memberNumber })}</span> : null}
              <span className="rounded-full px-3 py-1.5" style={subtleCardStyle}>@{(userProfile as any)?.username || (userProfile as any)?.handle || 'angler'}</span>
            </div>
          </div>
        </div>
      </section>

      <SettingsSection
        eyebrow={t('editProfile.publicProfile', 'Public profile')}
        title={t('editProfile.anglerCard', 'Angler card')}
        description={t('editProfile.publicProfileDescription', 'These details appear on your profile and community activity.')}
        style={cardStyle}
        mutedColor={muted}
      >
        <div className="grid gap-4">
          <div>
            <label htmlFor="name" className={labelClass} style={{ color: muted }}>{t('editProfile.displayName', 'Display name')}</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="roddo-mobile-form block w-full rounded-[14px] px-4 py-3 text-[15px] font-semibold outline-none"
              style={inputStyle}
              placeholder={t('editProfile.captainName', 'Captain name')}
            />
          </div>

          <div>
            <label htmlFor="bio" className={labelClass} style={{ color: muted }}>{t('editProfile.bio', 'Bio')}</label>
            <textarea
              id="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className="roddo-mobile-form min-h-[112px] w-full resize-none rounded-[14px] px-4 py-3 text-[15px] outline-none"
              style={inputStyle}
              rows={4}
              placeholder={t('editProfile.bioPlaceholder', 'Share your fishing style, favorite waters, target species, or what you are learning.')}
            />
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        eyebrow={t('editProfile.appPreferences', 'App preferences')}
        title={t('editProfile.language', 'Language')}
        description={t('editProfile.languageDescription', "Choose Roddo's app language preference. English is fully supported first; more translated screens are coming soon.")}
        style={cardStyle}
        mutedColor={muted}
      >
        <LanguageSwitcher
          ui={ui}
          inputStyle={inputStyle}
          panelStyle={cardStyle}
          value={preferredLanguage}
          onChange={setPreferredLanguage}
        />
        <p className="mt-3 text-[11px] font-semibold leading-5" style={{ color: muted }}>
          {t('editProfile.languageSavedNote', 'This saves your language preference across devices. Some Roddo screens may still display English while translations are completed.')}
        </p>
      </SettingsSection>

      <SettingsSection
        eyebrow={t('editProfile.fishiqPrivacy', 'Water read privacy')}
        title={t('editProfile.observationSharing', 'Observation sharing')}
        description={t('editProfile.observationDescription', 'Choose your default for contributing generalized fishing observations. You can still override this when logging a catch.')}
        style={cardStyle}
        mutedColor={muted}
        action={
          <button
            type="button"
            onClick={() => setFishiqContributionEnabled((prev) => !prev)}
            className="shrink-0 rounded-2xl px-3 py-2 text-xs font-black transition active:translate-y-[1px]"
            style={fishiqContributionEnabled ? primaryButtonStyle : quietButtonStyle}
            aria-pressed={fishiqContributionEnabled}
          >
            {fishiqContributionEnabled ? t('editProfile.on', 'On') : t('editProfile.off', 'Off')}
          </button>
        }
      >
        <button
          type="button"
          onClick={() => setFishiqContributionEnabled((prev) => !prev)}
          className="w-full rounded-[14px] p-3.5 text-left transition active:translate-y-[1px]"
          style={subtleCardStyle}
          aria-pressed={fishiqContributionEnabled}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-black">{t('editProfile.shareObservations', 'Share generalized observations with Roddo')}</div>
              <p className="mt-1 text-xs leading-5" style={{ color: muted }}>
                {t('editProfile.shareObservationsCopy', 'Your exact spot and identity are never shown. Roddo only uses privacy-protected summaries to improve local water intelligence.')}
              </p>
              <p className="mt-2 text-[11px] leading-4" style={{ color: muted }}>
                {t('editProfile.fishiqDisclaimer', "Roddo's fishing intelligence is educational and probabilistic. It is not official regulatory guidance, stock assessment data, or a guarantee of fish activity.")}
              </p>
            </div>
            <span className="shrink-0 pt-0.5">
              <span
                className="inline-flex h-6 w-11 items-center rounded-full px-[3px] transition-colors"
                style={{
                  background: fishiqContributionEnabled
                    ? '#34D399'
                    : isDark
                      ? 'rgba(255,255,255,0.10)'
                      : 'rgba(0,0,0,0.10)',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                }}
              >
                <span
                  className="h-5 w-5 rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,0.25)] transition-transform"
                  style={{ transform: fishiqContributionEnabled ? 'translateX(20px)' : 'translateX(0)' }}
                />
              </span>
            </span>
          </div>
        </button>
      </SettingsSection>



      {canOpenAdmin && (
        <button
          type="button"
          onClick={() => onOpenAdmin?.()}
          className="w-full rounded-[22px] px-4 py-3 text-sm font-black transition active:translate-y-[1px]"
          style={quietButtonStyle}
        >
          Admin
        </button>
      )}

      <section className="rounded-[14px] p-3" style={cardStyle}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="min-w-[108px] rounded-[14px] px-4 py-3 text-sm font-black transition active:translate-y-[1px]"
            style={quietButtonStyle}
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saveState === 'saving' || !hasChanges}
            className="flex-1 rounded-[14px] px-4 py-3 text-sm font-black transition active:translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-60"
            style={saveState === 'saving' || !hasChanges ? quietButtonStyle : primaryButtonStyle}
          >
            {saveState === 'saving' ? t('editProfile.saving', 'Saving…') : hasChanges ? t('common.saveChanges', 'Save changes') : t('editProfile.noChanges', 'No changes')}
          </button>
        </div>
      </section>

      {avatarLightboxOpen && (
        <div
          className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
          onClick={() => setAvatarLightboxOpen(false)}
          role="button"
          tabIndex={0}
        >
          <img
            src={avatarSrc}
            alt={t('editProfile.avatarFull', 'Avatar full')}
            className="block max-h-[90vh] max-w-[95vw] rounded-[28px] object-contain shadow-2xl"
            onClick={() => setAvatarLightboxOpen(false)}
          />
        </div>
      )}
    </div>
  );
};

export default EditProfileView;
